import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges,
  OnInit, Output, ChangeDetectorRef, OnDestroy, SimpleChanges} from '@angular/core';
import {BaseComponent} from '../../components/general/base/base.component';
import {Point} from 'geojson';
import {MapElement} from '../../shared/map/map-element.model';
import {LinkingService} from '../../linking/linking.service';
import {AuthService} from '../../auth/auth.service';
import { isFollowingAvailableForFeature } from '../../custom/vrgroup/follow-train/follow-utils';
import { Layer } from '../../shared/map/layer/layer.model';
import { last } from 'lodash-es';
import { InfluxService } from '~app/influx/influx.service';
import { JoensuuTenantConfig } from '~app/shared/map/layer/layer-definitions/T13';

interface VisibleButtonConfig {
  info: boolean;
  focus: boolean;
  flag: boolean;
  follow: boolean;
  measurements: boolean;
  cargoManifest: boolean;
}

@Component({
  selector: 'qm-element-actions',
  templateUrl: './element-actions.component.html',
  styleUrls: ['./element-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElementActionsComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  public selectedFeature: MapElement<Point>;
  @Input()
  public selectedLayer: Layer;
  @Input()
  public qmStyle: { [key: string]: string; };
  @Input()
  public visibleButtons: VisibleButtonConfig = {
      info: true,
      focus: true,
      flag: true,
      follow: true,
      measurements: true,
      cargoManifest: false,
  };
  @Output()
  public detailsOpened: EventEmitter<void> = new EventEmitter();

  public vivoId: string = null;
  /**
   * Username
   */
  public userName: string;
  private vivoType: string = 'Asset';

  public tenant: number;
  public showVrInfo: boolean = true;
  public hasMeasurements: boolean = false;

  private closed: boolean = false;

  /**
   * @param {MapService} mapService
   * @param {VivoService} vivoService
   */
  constructor(
    private readonly linkingService: LinkingService,
    private readonly influxService: InfluxService,
    private readonly auth: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
    this.name = 'ElementActionsComponent';
    this.userName = this.auth.getSubject();
    this.tenant = this.auth.getTenant();
  }

  ngOnInit() {
    this.updateVivoMapping();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateVivoMapping();
    const isSelectedFeatureOnMap = this.selectedFeature?.geometry?.coordinates
      && this.selectedFeature.geometry.coordinates[0] !== 0;
    this.visibleButtons.focus = isSelectedFeatureOnMap;
    this.visibleButtons.follow = isFollowingAvailableForFeature(this.selectedFeature) && isSelectedFeatureOnMap;

    if (changes.selectedFeature || changes.selectedLayer) {
      this.updateHasMeasurements();
    }
  }

  ngOnDestroy() {
    this.closed = true;
  }

  onDetailsOpened() {
    this.detailsOpened.emit();
  }

  public setVrInfoVisibility(show: boolean) {
    this.showVrInfo = show;
    if (!this.closed) {
      this.cdr.detectChanges();
    }
  }

  private async updateVivoMapping(): Promise<void> {
    if (this.selectedFeature) {
      // Use feature's id in cases that selectedLayer is not defined
      const regoId = this.selectedLayer !== undefined
        ? this.selectedFeature.properties[this.selectedLayer.metadata.uniqueBy]
        : this.selectedFeature.id;
      // TODO: handle multiple linkings
      const linkings = await this.linkingService.getLinkingsForRegoId(regoId, 'vivo');
      if (linkings.length > 0) {
        this.vivoId = last(linkings).sourceId;
        this.vivoType = last(linkings).sourceLinkType;
        this.cdr.detectChanges(); //// to get VIVO link visible ??
      }
    }
  }

  private async updateHasMeasurements(): Promise<void> {
    this.hasMeasurements = false;
    this.cdr.markForCheck();

    const config = this.auth.getTenantConfig();
    if (!config.influxMeasurements) {
      // this tenant doesn't use measurements at all
      return;
    }

    const measurementsFilters = this.selectedLayer?.metadata?.measurementsFilters;
    if (measurementsFilters) {
      const filters = {};
      for (const [fieldName, propertyName] of Object.entries(measurementsFilters)) {
        filters[fieldName] = this.selectedFeature.properties[propertyName];
      }
      this.hasMeasurements = await this.influxService.measurementExists(
        config.influxMeasurements.bucket,
        config.influxMeasurements.measurement,
        filters,
      );
      this.cdr.markForCheck();
    }
  }

  /**
   * Return link to VIVO for current map object
   * @returns {string}
   */
  public getVivoLink(): string {
    if (this.vivoType === 'Organization') {
      return 'https://miprovivo.trackinno.com/organizationNodes/' + this.vivoId + '/assets';
    } else {
      return 'https://miprovivo.trackinno.com/assets/' + this.vivoId;
    }
  }
}
